import { useModals } from '@any-ui-react/modals';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { ExportHistoryList } from './ExportHistoryList';
import type { ExportHistoryKind } from './types';

/**
 * Hook returning a function that opens the Export History modal.
 *
 * The modal manages its own lifecycle: it self-closes via its internal Close button
 * (calling `closeModal` with the generated `modalId`), so the caller does not pass an
 * `onClose` callback.
 *
 * @returns A function `(kind, title) => void` that opens the modal:
 *   - `kind`  — which export-history list to render (employee, performanceReviewList, ...).
 *   - `title` — required modal title (no default; every caller supplies an explicit one).
 */
export const useOpenExportHistoryModal = () => {
  const { openModal, closeModal } = useModals();

  return useCallback(
    (kind: ExportHistoryKind, title: string) => {
      const modalId = uuid();
      openModal({
        modalId,
        closeOnClickOutside: false,
        closeOnEscape: true,
        centered: false,
        size: '1024px',
        title,
        children: (
          <div className="-mx-6 -mt-4">
            <ExportHistoryList kind={kind} onClose={() => closeModal(modalId)} />
          </div>
        ),
      });
    },
    [openModal, closeModal],
  );
};
